import axios from 'axios';
import {
  addTokenToConsts,
  ALL_TOKENS,
  NATIVE_TOKENS,
  removeCustomTokenFromConsts,
  ROUTER_ADDRESSES,
  SUPPORTED_CHAINS,
  WOWMAX_API_URL,
  WRAPPED_NATIVE_TOKENS,
} from 'consts';
import { makeAutoObservable, reaction, runInAction } from 'mobx';
import { Web3Store } from 'store/web3/Web3Store';
import { Mode, TokenInfo, TxModalState } from 'types';
import { isValidSwapToken } from 'utils/helpers';
import { Address } from 'viem';

import { getFixedTokens, saveCustomTokens, saveFixedTokens } from './helpers';

const initialPricesMap: Record<number, Record<Address, string>> = {};
SUPPORTED_CHAINS.forEach((c) => {
  initialPricesMap[c] = {};
});

const UPDATE_PRICES_SEC = 5 * 60; // 5 min

const MOBILE_WIDTH = 600;
const TABLET_WIDTH = 1000;

const getTokens = (chainId: number) => ALL_TOKENS[chainId]?.filter(isValidSwapToken) || [];

export class CommonStore {
  mode: Mode = 'main';

  isMobile: boolean = false;
  isTablet: boolean = false;
  isBrowserTabActive = true;

  tokens: TokenInfo[] = [];
  fixedTokens: Address[] = [];

  pricesMap: Record<number, Record<Address, string>> = initialPricesMap;
  private pricesTimerId: ReturnType<typeof setTimeout> | null = null;

  requestingChainId: null | number = null;
  txModal: TxModalState | null = null;

  constructor(private web3: Web3Store) {
    makeAutoObservable(this, {}, { autoBind: true });

    if (typeof window !== 'undefined') {
      this.tokens = getTokens(this.web3.chainId);
      this.fixedTokens = getFixedTokens(this.web3.chainId);

      window.addEventListener('resize', () => {
        runInAction(() => {
          this.isMobile = window.innerWidth <= MOBILE_WIDTH;
          this.isTablet = window.innerWidth <= TABLET_WIDTH;
        });
      });
      this.isMobile = window.innerWidth <= MOBILE_WIDTH;
      this.isTablet = window.innerWidth <= TABLET_WIDTH;

      document.addEventListener(
        'visibilitychange',
        () => {
          runInAction(() => {
            this.isBrowserTabActive = document.visibilityState !== 'hidden';
          });
        },
        false
      );

      this.checkSwapRouterContracts();
    }

    reaction(
      () => this.web3.chainId,
      (chainId) => {
        this.tokens = getTokens(chainId);
        this.fixedTokens = getFixedTokens(chainId);

        clearTimeout(this.pricesTimerId || undefined);
        this.pricesTimerId = null;
      }
    );

    reaction(
      () => Object.values(this.web3.balances).length,
      (balancesLength) => {
        const isBalancesReady = balancesLength > 2;

        if (!isBalancesReady || this.web3.isDevnet) {
          clearTimeout(this.pricesTimerId || undefined);
          this.pricesTimerId = null;
          return;
        }
        if (this.pricesTimerId === null) {
          this.fetchPrices();
        }
      }
    );
  }

  setMode = (mode: Mode) => {
    this.mode = mode;
  };

  toggleMode = (mode: Mode) => {
    this.mode = this.mode === mode ? 'main' : mode;
  };

  resetMode = () => {
    this.mode = 'main';
  };

  get prices() {
    return this.pricesMap[this.web3.chainId] || {};
  }

  fetchPrices = async () => {
    clearTimeout(this.pricesTimerId || undefined);
    const chainId = this.web3.chainId;
    const prices: Record<Address, string> = {};

    try {
      const native = this.web3.nativeToken.address;
      const wrappedNative = this.web3.wrappedNativeToken.address;

      const tokens = [
        ...Object.entries(this.web3.balances)
          .filter(
            ([address, balance]) =>
              balance !== undefined &&
              balance !== '0' &&
              address !== native &&
              address !== wrappedNative
          )
          .map(([address]) => address),
        wrappedNative,
      ];

      const result = await axios.post<{ Address: Address; Price: number }[]>(
        `${WOWMAX_API_URL}/chains/${chainId}/prices`,
        { baseTokens: tokens }
      );

      result.data.forEach((t) => {
        prices[t.Address] = t.Price.toString();
        if (t.Address === wrappedNative) {
          prices[native] = t.Price.toString();
        }
      });
    } catch (err) {
      if (axios.isCancel(err)) {
        console.log('Request canceled: prices');
      } else {
        console.error(err);
      }
    }

    runInAction(() => {
      this.pricesMap[chainId] = { ...this.prices, ...prices };
      this.pricesTimerId = setTimeout(this.fetchPrices, UPDATE_PRICES_SEC * 1000);
    });
  };

  updatePrice = (chainId: number, address: Address, price: string | null) => {
    if (!this.pricesMap[chainId]) this.pricesMap[chainId] = {};
    if (!price) {
      delete this.pricesMap[chainId][address];
    } else {
      this.pricesMap[chainId][address] = price;
      const native = NATIVE_TOKENS[chainId]?.address;
      const wrapped = WRAPPED_NATIVE_TOKENS[chainId]?.address;
      if (address === native && wrapped) {
        this.pricesMap[chainId][wrapped] = price;
      } else if (address === wrapped && native) {
        this.pricesMap[chainId][native] = price;
      }
    }
  };

  get customTokens() {
    return this.tokens.filter((t) => t.tags?.includes('custom'));
  }

  addCustomToken = (token: TokenInfo) => {
    addTokenToConsts(token, true);
    this.tokens = [...this.tokens, token];
    saveCustomTokens(this.web3.chainId, this.customTokens);
    this.addFixedToken(token.address);
  };

  removeCustomToken = (token: TokenInfo) => {
    removeCustomTokenFromConsts(token);
    this.tokens = this.tokens.filter((t) => t.address !== token.address);
    saveCustomTokens(this.web3.chainId, this.customTokens);
    this.removeFixedToken(token.address);
  };

  addFixedToken = (address: Address) => {
    this.fixedTokens = [...this.fixedTokens, address];
    saveFixedTokens(this.web3.chainId, this.fixedTokens);
  };

  removeFixedToken = (address: Address) => {
    this.fixedTokens = this.fixedTokens.filter((t) => t !== address);
    saveFixedTokens(this.web3.chainId, this.fixedTokens);
  };

  setRequestingChainId = (chainId: number | null) => {
    this.requestingChainId = chainId;
  };

  openTxModal = (state: TxModalState) => {
    this.txModal = state;
  };

  closeTxModal = () => {
    this.txModal = null;
  };

  checkSwapRouterContracts = async () => {
    try {
      const response = await axios.get<{ id: number; wowmaxRouter: Address }[]>(
        `${WOWMAX_API_URL}/chains`
      );
      response.data.forEach((chain) => {
        ROUTER_ADDRESSES[chain.id] = chain.wowmaxRouter;
      });
      // eslint-disable-next-line no-empty
    } catch {}
  };
}
